import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { VerificationFlow } from '@ory/client';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxInputComponent } from '@ui/input/input.component';
import { timer } from 'rxjs';
import {
  csrfToken,
  flowMessages,
  flowValue,
  submitFlow,
} from 'src/app/services/authentication/kratos-flow';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { LoadUserAction } from 'src/app/store/user/user.actions';
import { AuthHeaderComponent } from '../components/auth-header/auth-header.component';
import { AuthHelpComponent } from '../components/auth-help/auth-help.component';
import { AuthPanelComponent } from '../components/auth-panel/auth-panel.component';
import { AuthShellComponent } from '../components/auth-shell/auth-shell.component';
import { CodeEntryComponent } from 'src/app/core/components/code-entry/code-entry.component';
import { codeStep } from 'src/app/core/components/code-entry/code-step';
import { CodeResendComponent } from '../components/code-resend/code-resend.component';
import { resendCooldown } from '../components/code-resend/resend-cooldown';
import { KratosMessagesComponent } from 'src/app/core/components/kratos-messages/kratos-messages.component';

/** How long "Email verified" shows before the app opens on its own. */
const CONTINUE_AFTER_MS = 1500;

/**
 * Email verification (Kratos `use: code`): the address gets a 6-digit
 * code. Kratos sends the mail's link here as `?flow=…` (already verified)
 * or `?flow=…&code=…`, which submits right away.
 */
@Component({
  selector: 'app-verification',
  templateUrl: './verification.component.html',
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    AuthHeaderComponent,
    AuthHelpComponent,
    AuthPanelComponent,
    AuthShellComponent,
    CodeEntryComponent,
    CodeResendComponent,
    KratosMessagesComponent,
    VxButtonComponent,
    VxFormFieldComponent,
    VxInputComponent,
  ],
})
export class VerificationPageComponent implements OnInit {
  private readonly auth = inject(KratosAuthenticationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly translation = inject(TranslationService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly flow = signal<VerificationFlow | undefined>(undefined);

  protected readonly stage = computed(() => {
    switch (this.flow()?.state) {
      case 'passed_challenge':
        return 'done';
      case 'sent_email':
        return 'code';
      default:
        return 'email';
    }
  });

  /** The address Kratos sent the code to. */
  protected readonly email = computed(() => {
    const flow = this.flow();
    return flow ? flowValue(flow, 'email') : '';
  });

  /** "A code was sent" / "verified" is what the page already says. */
  protected readonly messages = computed(() => {
    const flow = this.flow();
    return flow
      ? flowMessages(flow).filter(message => message.type === 'error')
      : [];
  });

  protected readonly emailForm = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
  });

  protected readonly step = codeStep();

  protected readonly cooldown = resendCooldown();

  /** A step failed without Kratos saying why (network, Kratos down). */
  protected readonly failed = signal(false);

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.load(params.get('flow'), params.get('code'));
  }

  /** The mail's flow (submitting its code), or a fresh one. */
  private async load(flowId: string | null, code: string | null) {
    const flow = flowId
      ? await this.auth.loadVerificationFlow(flowId).catch(() => undefined)
      : undefined;
    if (!flow) {
      await this.openFlow();
      return;
    }
    this.flow.set(flow);
    if (flow.state === 'passed_challenge') {
      this.continueSoon();
    } else if (flow.state === 'sent_email' && code) {
      this.step.code.setValue(code);
      this.submitCode(code);
    }
  }

  protected sendCode(): void {
    const { email } = this.emailForm.controls;
    if (email.invalid) {
      email.markAsTouched();
      return;
    }
    this.requestCode(email.value.trim());
  }

  protected resend(): void {
    if (this.cooldown.remaining() === 0) {
      this.requestCode(this.email());
    }
  }

  protected async submitCode(code: string): Promise<void> {
    const flow = this.flow();
    if (!flow) {
      return;
    }
    const next = await this.step.verify(() =>
      this.submit(flow, { method: 'code', code, csrf_token: csrfToken(flow) })
    );
    // nothing to check against (busy), or no flow back: "failed" says so
    if (!next) {
      return;
    }
    if (next.state === 'passed_challenge') {
      this.continueSoon();
    } else {
      this.step.reject();
    }
  }

  /** Back to the address, with a fresh flow (the old code stops working). */
  protected changeEmail(): void {
    this.emailForm.controls.email.setValue(this.email());
    this.openFlow();
  }

  protected continue(): void {
    this.store.dispatch(new LoadUserAction());
    this.router.navigate(['/']);
  }

  /** Sending the address (again) mails a new code. */
  private async requestCode(email: string): Promise<void> {
    const next = await this.step.run(async () => {
      const flow = this.flow() ?? (await this.openFlow());
      return flow
        ? this.submit(flow, {
            method: 'code',
            email,
            csrf_token: csrfToken(flow),
            transient_payload: {
              flow_id: flow.id,
              locale: this.translation.currentLocale,
            },
          })
        : null;
    });
    if (next?.state === 'sent_email') {
      this.step.reset();
      this.cooldown.start();
    }
  }

  private async openFlow(): Promise<VerificationFlow | undefined> {
    this.failed.set(false);
    try {
      const flow = await this.auth.createVerificationFlow();
      this.flow.set(flow);
      return flow;
    } catch {
      this.failed.set(true);
      return undefined;
    }
  }

  /**
   * Kratos re-renders the flow on success and on a wrong code alike; the
   * page shows that flow. Null when there is none (network, expired).
   */
  private async submit(
    flow: VerificationFlow,
    body: Parameters<KratosAuthenticationService['updateVerificationFlow']>[1]
  ): Promise<VerificationFlow | null> {
    this.failed.set(false);
    const result = await submitFlow<VerificationFlow, VerificationFlow>(() =>
      this.auth.updateVerificationFlow(flow.id, body)
    );
    const next = result.ok ? result.value : result.flow;
    if (next) {
      this.flow.set(next);
    } else {
      this.failed.set(true);
    }
    return next;
  }

  private continueSoon(): void {
    timer(CONTINUE_AFTER_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.continue());
  }
}
